import { unref } from 'vue';
import { DIAS_SEMANA_REVERSE_MAP } from '../constants';
import { ensureTimeWithSeconds, formatDateTimeForApi } from '../utils/dateUtils';

export function normalizeDiaSemana(dia) {
  if (typeof dia === 'number') return dia;

  if (typeof dia === 'string') {
    const normalized = dia
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (DIAS_SEMANA_REVERSE_MAP.hasOwnProperty(normalized)) {
      return DIAS_SEMANA_REVERSE_MAP[normalized];
    }

    const numeric = parseInt(normalized, 10);
    return Number.isNaN(numeric) ? null : numeric;
  }

  if (dia && typeof dia === 'object') {
    if (typeof dia.dia_semana_id !== 'undefined') {
      return dia.dia_semana_id;
    }
    if (typeof dia.id !== 'undefined') {
      return dia.id;
    }
  }

  return null;
}

export function resolveFilialId(valor) {
  if (valor === undefined || valor === null) return null;

  if (typeof valor === 'object') {
    if (valor.idfilial) return valor.idfilial;
    if (valor.id) return valor.id;
  }

  if (typeof valor === 'string' && valor.trim() === '') return null;

  return valor;
}

export default function useNovaCotacaoPayload(formData) {
  const buildProdutosPayload = () => {
    const produtos = unref(formData.produtos);
    if (!Array.isArray(produtos)) return [];

    return produtos.map((item) => ({
      cotacao_produtos_id: item.cotacao_produtos_id,
      ean_gtin: item.ean_gtin || item.codigo_barras || '0',
      sku_systock: item.sku_systock || item.idproduto || null,
      sku_fornecedor: item.sku_fornecedor || item.cod_produto || '',
      descricao: item.descricao || item.descricao_produto || '',
      qtd_minima_aceita_Emb: item.qtd_minima_aceita_Emb || item.qtd_minima_aceita_emb || '0',
      qtd_pedida: item.qtd_pedida || '0',
      qtd_maxima_aceita_Emb: item.qtd_maxima_aceita_Emb || item.qtd_maxima_aceita_emb || '0',
      um_minima: item.um_minima || item.idunidade_medida || item.sigla_unidade || '',
      qtd_pedida_na_um_master: item.qtd_pedida_na_um_master || item.multiplo_compra || '0',
      um_master: item.um_master || item.unidade_master || item.sigla_unidade_master || '',
      fator_de_conversao_minima_master: item.fator_de_conversao_minima_master || item.fator_conversao || '0',
      preco_referencia: item.preco_referencia || item.preco_custo || '0',
    }));
  };

  const buildFornecedoresPayload = () => {
    const fornecedoresVal = unref(formData.fornecedores);
    if (!Array.isArray(fornecedoresVal)) return [];

    return fornecedoresVal
      .map((fornecedor) => {
        const representanteLista = Array.isArray(fornecedor.representantes)
          ? fornecedor.representantes
          : [];

        // Filtrar apenas representantes que participam da cotação
        const representantesParticipantes = representanteLista.filter(rep => {
          const participa = rep.participa;
          return participa === true || participa === 'true' || participa === 1;
        });

        // Formatar representantes participantes
        const representantesFormatados = representantesParticipantes.map(rep => ({
          cotacao_representante_fornecedor_id:
            rep.cotacao_representante_fornecedor_id ||
            rep.id ||
            rep.representante_id ||
            null,
          email: rep.email || '',
          telefone: rep.telefone || '',
          nome_representante: rep.nome_representante || rep.nome || '',
          cargo_representante: rep.cargo_representante || rep.cargo || '',
        }));

        // Se não houver contato principal no fornecedor, usar o primeiro representante participante
        // Também tenta usar o primeiro representante se o contato principal estiver incompleto
        const primeiroRep = representantesParticipantes.length > 0 ? representantesParticipantes[0] : null;

        const payload = {
          idfornecedor:
            fornecedor.idfornecedor ||
            fornecedor.id ||
            fornecedor.fornecedor_id ||
            null,
          email: fornecedor.email || fornecedor.email_fornecedor || (primeiroRep ? (primeiroRep.email || '') : ''),
          telefone: fornecedor.telefone || fornecedor.telefone_celular || (primeiroRep ? (primeiroRep.telefone || '') : ''),
          nome_contato: fornecedor.nome_contato || (primeiroRep ? (primeiroRep.nome_representante || primeiroRep.nome || '') : ''),
          cargo_contato: fornecedor.cargo_contato || (primeiroRep ? (primeiroRep.cargo_representante || primeiroRep.cargo || '') : ''),
        };

        // Garantir que campos obrigatórios de contato não fiquem vazios se houver qualquer representante participante
        if (primeiroRep) {
          if (!payload.email && primeiroRep.email) payload.email = primeiroRep.email;
          if (!payload.telefone && primeiroRep.telefone) payload.telefone = primeiroRep.telefone;
          if (!payload.nome_contato && (primeiroRep.nome_representante || primeiroRep.nome)) {
            payload.nome_contato = primeiroRep.nome_representante || primeiroRep.nome;
          }
          if (!payload.cargo_contato && (primeiroRep.cargo_representante || primeiroRep.cargo)) {
            payload.cargo_contato = primeiroRep.cargo_representante || primeiroRep.cargo;
          }
        }

        // Adicionar representantes apenas se houver participantes
        if (representantesFormatados.length > 0) {
          payload.representantes = representantesFormatados;
        }

        return payload;
      })
      .filter((fornecedor) => fornecedor.idfornecedor);
  };

  const buildSeguidoresPayload = () => {
    const idsSelecionados = Array.isArray(unref(formData.dadosGerais).seguidores)
      ? unref(formData.dadosGerais).seguidores
      : [];

    if (idsSelecionados.length > 0) {
      const unicos = Array.from(new Set(idsSelecionados));
      return unicos.map((userId) => ({ user_id: userId }));
    }

    const seguidoresVal = unref(formData.seguidores);
    if (Array.isArray(seguidoresVal) && seguidoresVal.length > 0) {
      return seguidoresVal.map((seguidor) => ({ user_id: seguidor.id }));
    }

    return [];
  };

  const buildCotacaoPayload = (options = {}) => {
    const { forcePublicar = false } = options;
    const c = unref(formData.cotacao) || {};
    const dg = unref(formData.dadosGerais);

    const titulo = dg.titulo || c.cotacao_titulo || '';
    const identificadorExterno = c.identificador_externo || null;
    const publicarFromApi = typeof c.publicar_cotacao === 'boolean' ? c.publicar_cotacao : false;
    const publicar = typeof forcePublicar === 'boolean' ? forcePublicar : publicarFromApi;

    return {
      cotacao_titulo: titulo,
      identificador_externo: identificadorExterno,
      publicar_cotacao: publicar,
    };
  };

  const buildConfigPayload = () => {
    const configVal = unref(formData.config);
    const dg = unref(formData.dadosGerais);
    const configAtual = Array.isArray(configVal) && configVal.length > 0
      ? configVal[0]
      : null;

    const inicio = formatDateTimeForApi(dg.data_inicio, dg.hora_inicio)
      || (configAtual && configAtual.inicio_periodo_recebimento_proposta) || null;

    const termino = formatDateTimeForApi(dg.data_fim, dg.hora_fim)
      || (configAtual && configAtual.termino_periodo_recebimento_proposta) || null;

    const idFilial =
      resolveFilialId(dg.empresa) ||
      (configAtual && configAtual.idfilial) ||
      null;

    if (!inicio || !termino || !idFilial) {
      return null;
    }

    const lembreDiasRaw = dg.lembrete_diario_dias;
    const lembreDiasParsed = lembreDiasRaw === '' || lembreDiasRaw === null || typeof lembreDiasRaw === 'undefined'
      ? NaN
      : parseInt(String(lembreDiasRaw), 10);
    const lembrete_diario_dias = Number.isFinite(lembreDiasParsed) && lembreDiasParsed >= 0 ? lembreDiasParsed : null;
    const lembrete_diario = dg.lembrete_diario === true && lembrete_diario_dias !== null && lembrete_diario_dias > 0;

    return {
      idfilial: idFilial,
      inicio_periodo_recebimento_proposta: inicio,
      termino_periodo_recebimento_proposta: termino,
      lembrete_diario,
      lembrete_diario_dias,
    };
  };

  const buildLocaisRecebimentoPayload = () => {
    const locais = unref(formData.locaisRecebimento);
    if (!Array.isArray(locais)) return [];

    const dg = unref(formData.dadosGerais);
    const prazoRaw = dg.prazo_entrega_desejado_dias;
    const prazoParsed =
      prazoRaw === '' || prazoRaw === null || typeof prazoRaw === 'undefined'
        ? NaN
        : parseInt(String(prazoRaw), 10);
    const prazo_entrega_desejado_dias =
      Number.isFinite(prazoParsed) && prazoParsed >= 1 ? prazoParsed : null;

    return locais.map((local) => {
      const dias = Array.isArray(local.dias)
        ? local.dias
          .map((dia) => normalizeDiaSemana(dia))
          .filter((dia) => dia !== null && dia !== undefined)
        : [];

      const filialId =
        resolveFilialId(local.idfilial) ||
        resolveFilialId(local.filial) ||
        resolveFilialId(dg.empresa);

      const horaInicio = local.inicio || local.hora_recebimento_inicio || '';
      const horaFim = local.fim || local.hora_recebimento_fim || '';
      const observacao = local.observacao || local.observacao_recebimento || '';

      return {
        cotacao_local_recebimento_id:
          local.cotacao_local_recebimento_id || local.id || null,
        idfilial: filialId,
        endereco: local.endereco || local.titulo || '',
        hora_recebimento_inicio: ensureTimeWithSeconds(horaInicio),
        hora_recebimento_fim: ensureTimeWithSeconds(horaFim),
        dias,
        observacao_recebimento: observacao,
        prazo_entrega_desejado_dias,
      };
    });
  };

  const montarPayload = (options = {}) => {
    const payload = {};

    const cotacaoPayload = buildCotacaoPayload(options);
    if (cotacaoPayload) {
      payload.cotacao = cotacaoPayload;
    }

    const produtosPayload = buildProdutosPayload();
    if (produtosPayload.length > 0) {
      payload.produtos = produtosPayload;
    }

    const fornecedoresPayload = buildFornecedoresPayload();
    if (fornecedoresPayload.length > 0) {
      payload.fornecedores = fornecedoresPayload;
    }

    const seguidoresPayload = buildSeguidoresPayload();
    if (seguidoresPayload.length > 0) {
      payload.seguidores = seguidoresPayload;
    }

    const configPayload = buildConfigPayload();
    if (configPayload) {
      payload.config = configPayload;
    }

    const locaisPayload = buildLocaisRecebimentoPayload();
    if (locaisPayload.length > 0) {
      payload.locais_recebimento = locaisPayload;
    }

    const dg = unref(formData.dadosGerais);
    if (dg.responsavel) {
      payload.responsavel = {
        user_id: dg.responsavel
      };
    }

    return payload;
  };

  return {
    buildProdutosPayload,
    buildFornecedoresPayload,
    buildSeguidoresPayload,
    buildCotacaoPayload,
    buildConfigPayload,
    buildLocaisRecebimentoPayload,
    montarPayload,
    // Helpers exportados
    normalizeDiaSemana,
    resolveFilialId
  };
}
